import React, { useState, useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { EditorLink } from '@utils/tiptapEditorLink';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { toolbarActions } from './toolbarActions';
import { setPostImage, commonImage } from '@images/imageUrls';
import EmojiPicker from '@components/EmojiPicker/pc';
import MentionPicker from '@components/MentionPicker/pc';
import TopicPicker from '@components/TopicPicker/pc';
import VoteModal from '@components/VoteModal/pc';
import { uploadAttachment } from '../../../../../server/api';
import { uploadOssFile } from '../../../../../server/oss-upload';
import Toast from '@components/Toast';
import styles from '../index.module.scss';
import './TiptapEditor.scss'; // 引入全局样式
import { MAX_THREAD_IMAGES, countNormalImagesFromHtml } from '../../../../../utils/setPostLimits';
import { getMentionLimit, mergeMentions } from '../../../../../utils/setPostMentions';
import { isSupportedImageFile } from '../../../../../utils/imageFile';
import type { Topic } from '@components/TopicPicker/pc';
import { replaceEmojiCodes } from '../../../../../utils/emoji';
import { prepareEditorHtmlForHydration } from '../../../../../utils/tiptapEditorContent';
import { pasteContent, getContentType } from '@utils/paste';

const COLOR_LIST = [
    '#222', '#666', '#b1b3b6', '#003087', '#2254f4', '#002b7f', '#e02020', '#ffb800'
];

// 白名单校验函数
const validateLinkWithWhitelist = (url: string, whiteList: string[], whiteHttpList: string[]): boolean => {
    console.log('[TiptapEditor] validateLinkWithWhitelist - 开始验证链接:', url);
    console.log('[TiptapEditor] validateLinkWithWhitelist - 白名单数据:', { whiteList, whiteHttpList });

    try {
        // 1. 检查是否是 https:// 后面跟 deeplink 的情况（如 https://iqoo://com.iqoo.bbs/entrance）
        const httpsDeeplink = url.match(/^https:\/\/([a-z0-9\.\-\+]+:\/\/.+)/i);
        if (httpsDeeplink) {
            const deeplink = httpsDeeplink[1];
            const isWhite = whiteList.some(scheme => deeplink.startsWith(scheme));
            console.log('[TiptapEditor] validateLinkWithWhitelist - HTTPS deeplink检查:', { deeplink, isWhite });
            if (isWhite) return true;
        }

        // 2. 检查是否是直接的 deeplink 协议（非 http/https）
        const isDeeplink = /^[a-z0-9\.\-\+]+:\/\//i.test(url) && !/^https?:\/\//i.test(url);
        if (isDeeplink) {
            const isWhite = whiteList.some(scheme => url.startsWith(scheme));
            console.log('[TiptapEditor] validateLinkWithWhitelist - 直接deeplink检查:', { isDeeplink, isWhite });
            if (isWhite) return true;
        }

        // 3. 检查 HTTP/HTTPS 链接的域名是否在白名单中
        if (/^https?:\/\//i.test(url)) {
            const urlObj = new URL(url);
            const host = urlObj.host.toLowerCase();
            // 域名白名单支持主域名和二级域名
            const isWhite = whiteHttpList.some(domain =>
                host === domain ||
                host.endsWith('.' + domain)
            );
            console.log('[TiptapEditor] validateLinkWithWhitelist - HTTP/HTTPS域名检查:', { url, host, whiteHttpList, isWhite });
            if (isWhite) return true;
        }

        console.log('[TiptapEditor] validateLinkWithWhitelist - 验证失败，所有检查均未通过');
        return false;
    } catch (error) {
        console.error('[TiptapEditor] validateLinkWithWhitelist - 验证出错:', error);
        // URL 解析失败，视为无效
        return false;
    }
};

export interface TiptapEditorProps {
    content?: string;
    onContentChange?: (content: string) => void;
    onVideoUpload?: (videoSrc: string, videoFile?: File, videoData?: any) => void;
    onVoteCreate?: (voteData: any) => void;
    showVoteModal?: boolean;
    onVoteModalClose?: () => void;
    onVoteModalOpen?: () => void;
    voteData?: any;
    topicList: Topic[];
    categoryList?: string[];
    onTopicSelect?: (topics: Topic[]) => void;
    onCategoryChange?: (categoryName: string, page?: number) => void;
    selectedTopicIds?: Array<string | number>; // 添加初始选中的话题ID
    topicCache?: { [key: string]: any }; // 话题缓存，用于显示真实名称
    hasVideo?: boolean; // 是否已有视频
    topicStore?: any; // 话题店铺实例，用于搜索
    whiteList?: string[]; // 白名单协议列表（deeplink）
    whiteHttpList?: string[]; // 白名单域名列表（HTTP/HTTPS）
    hasMore?: boolean;
    isLoadingMore?: boolean;
    otherPermissions?: { // 权限对象
        canInsertThreadImage?: boolean;
        canInsertThreadVideo?: boolean;
        canInsertThreadVote?: boolean;
    };
    currentMentions?: Array<{ id: string; name: string; [key: string]: any }>;
    currentUserInfo?: any;
    emojiList?: any[];
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({
    content = '',
    onContentChange,
    onVideoUpload,
    onVoteCreate,
    showVoteModal: externalShowVoteModal,
    onVoteModalClose,
    onVoteModalOpen,
    voteData: externalVoteData,
    topicList,
    categoryList = [],
    onTopicSelect,
    onCategoryChange,
    selectedTopicIds = [],
    topicCache = {},
    hasVideo = false,
    topicStore,
    whiteList = [],
    whiteHttpList = [],
    hasMore = true,
    isLoadingMore = false,
    otherPermissions = {},
    currentMentions = [],
    currentUserInfo,
    emojiList = [],
}) => {
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const [pendingEditor, setPendingEditor] = useState<any>(null);
    const [showColorModal, setShowColorModal] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiPickerPosition, setEmojiPickerPosition] = useState<{ top: number; left: number }>({ top: 80, left: 0 });
    const [showMentionPicker, setShowMentionPicker] = useState(false);
    const [showTopicPicker, setShowTopicPicker] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState<any[]>([]);

    // 监听 showTopicPicker 变化，用于调试
    useEffect(() => {
        console.log('[TiptapEditor] showTopicPicker changed to:', showTopicPicker, 'topicList length:', topicList.length, 'categoryList length:', categoryList.length);
    }, [showTopicPicker]);
    const [internalShowVoteModal, setInternalShowVoteModal] = useState(false);
    const [, setRefreshTick] = useState(0);
    const [uploadingImages, setUploadingImages] = useState(false);
    const colorModalRef = useRef<HTMLDivElement>(null);
    const colorTriggerRef = useRef<HTMLDivElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const toolbarWrapRef = useRef<HTMLDivElement>(null);
    const lastHydratedSourceRef = useRef<string | null>(null);
    const lastHydratedHtmlRef = useRef<string>('');

    // 使用外部传入的showVoteModal或内部状态
    const showVoteModal = externalShowVoteModal !== undefined ? externalShowVoteModal : internalShowVoteModal;

    const updateEmojiPickerPosition = (target: HTMLElement) => {
        const container = toolbarWrapRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const pickerWidth = 375;
        const nextTop = targetRect.bottom - containerRect.top + 12;
        const maxLeft = Math.max(containerRect.width - pickerWidth, 0);
        const nextLeft = Math.min(Math.max(targetRect.left - containerRect.left, 0), maxLeft);

        setEmojiPickerPosition({ top: nextTop, left: nextLeft });
    };

    // 同步外部传入的选中话题
    useEffect(() => {
        console.log('[TiptapEditor] sync topics:', { selectedTopicIds, topicListLength: topicList.length, currentSelectedCount: selectedTopics.length, topicCacheSize: Object.keys(topicCache).length });

        if (!selectedTopicIds || selectedTopicIds.length === 0) {
            // 清空选中的话题
            if (selectedTopics.length > 0) {
                console.log('[TiptapEditor] clearing selected topics');
                setSelectedTopics([]);
            }
            return;
        }

        // 检查当前选中的话题ID是否与外部传入的一致
        const currentIds = selectedTopics.map(t => String(t.id)).sort().join(',');
        const externalIds = selectedTopicIds.map(id => String(id)).sort().join(',');

        if (currentIds === externalIds) {
            // ID一致，只需要更新话题详情（如果从占位符变成了真实话题）
            const updatedTopics = selectedTopics.map(selected => {
                const stringId = String(selected.id);
                // 如果是占位符（name 包含"话题"），尝试从缓存或列表中找到真实话题
                if (selected.name && selected.name.startsWith('话题 ')) {
                    // 优先从缓存中查找
                    if (topicCache[stringId]) {
                        return topicCache[stringId];
                    }
                    // 再从列表中查找
                    const realTopic = topicList.find(t => String(t.id) === stringId);
                    if (realTopic) {
                        console.log('[TiptapEditor] upgrading placeholder to real topic:', realTopic);
                        return realTopic;
                    }
                }
                return selected;
            });

            // 只有在确实有占位符被升级时才更新
            const hasUpgrade = updatedTopics.some((t, i) => t !== selectedTopics[i]);
            if (hasUpgrade) {
                setSelectedTopics(updatedTopics);
            }
            return;
        }

        // ID不一致，需要重新同步
        console.log('[TiptapEditor] IDs changed, re-syncing. Current:', currentIds, 'External:', externalIds);

        // 先从列表中查找匹配的话题，保留现有的真实话题对象
        const preset: any[] = [];
        const foundIds = new Set<string>();

        // 第一步：尝试在列表中找到真实话题，优先保留现有的真实话题对象
        selectedTopicIds.forEach(searchId => {
            const stringSearchId = String(searchId);

            // 先检查是否已经在 selectedTopics 中（已有真实对象）
            const existingTopic = selectedTopics.find(t => String(t.id) === stringSearchId);
            if (existingTopic && existingTopic.name && !existingTopic.name.startsWith('话题 ')) {
                // 这是一个真实的话题对象，保留它
                preset.push(existingTopic);
                foundIds.add(stringSearchId);
                console.log(`[TiptapEditor] preserved existing topic:`, existingTopic);
                return;
            }

            // 再尝试从缓存中查找
            if (topicCache[stringSearchId]) {
                preset.push(topicCache[stringSearchId]);
                foundIds.add(stringSearchId);
                console.log(`[TiptapEditor] matched topic from cache:`, topicCache[stringSearchId]);
                return;
            }

            // 最后尝试从 topicList 中查找
            const found = topicList.find(t => String(t.id) === stringSearchId);
            if (found) {
                preset.push(found);
                foundIds.add(stringSearchId);
                console.log(`[TiptapEditor] matched topic from list:`, found);
            }
        });

        // 第二步：对于未找到的ID，才创建占位符
        selectedTopicIds.forEach(searchId => {
            const stringSearchId = String(searchId);
            if (!foundIds.has(stringSearchId)) {
                // 尝试从缓存中获取话题信息（编辑模式下很可能话题信息已在缓存中）
                const cachedTopic = topicCache[stringSearchId];
                if (cachedTopic) {
                    preset.push(cachedTopic);
                    foundIds.add(stringSearchId);
                    console.log(`[TiptapEditor] found topic in cache for id ${searchId}:`, cachedTopic);
                } else {
                    // 创建占位符，但尽量显示有意义的信息
                    const placeholder: any = {
                        id: stringSearchId,
                        name: `话题 ${searchId}`,
                        cover: commonImage.official2,
                        count: '加载中...',
                    };
                    preset.push(placeholder);
                    console.log(`[TiptapEditor] created placeholder for id:`, searchId);
                }
            }
        });

        console.log('[TiptapEditor] final preset:', preset);
        if (preset.length > 0) {
            setSelectedTopics(preset);
        }
    }, [selectedTopicIds, topicList, topicCache]);

    // 点击编辑器或页面其他地方关闭颜色选择框
    useEffect(() => {
        if (!showColorModal) return;
        const handleClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                colorModalRef.current &&
                !colorModalRef.current.contains(target) &&
                !(colorTriggerRef.current && colorTriggerRef.current.contains(target))
            ) {
                setShowColorModal(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showColorModal]);

    useEffect(() => {
        if (!showColorModal) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowColorModal(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showColorModal]);

    // 扩展 Image，支持 data-emoji / width / height 属性渲染
    const CustomImage = Image.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                width: {
                    default: null,
                    parseHTML: element => element.getAttribute('width'),
                    renderHTML: attributes => (attributes.width ? { width: attributes.width } : {}),
                },
                height: {
                    default: null,
                    parseHTML: element => element.getAttribute('height'),
                    renderHTML: attributes => (attributes.height ? { height: attributes.height } : {}),
                },
                'data-emoji': {
                    default: null,
                    parseHTML: element => element.getAttribute('data-emoji'),
                    renderHTML: attributes => (attributes['data-emoji'] ? { 'data-emoji': attributes['data-emoji'] } : {}),
                },
            } as any;
        },
    });

    // 扩展 Mention，支持解析旧格式的 data-id
    const CustomMention = Mention.extend({
        addAttributes() {
            return {
                id: {
                    default: null,
                    parseHTML: element => element.getAttribute('data-code') || element.getAttribute('data-id'),
                    renderHTML: attributes => (attributes.id ? { 'data-code': attributes.id } : {}),
                },
                label: {
                    default: null,
                    parseHTML: element => {
                        const text = element.textContent || '';
                        return text.replace(/^@/, '').trim();
                    },
                    renderHTML: attributes => ({}),
                },
            };
        },
        parseHTML() {
            return [
                {
                    tag: 'span[class*="member"]',
                    getAttrs: (element: any) => {
                        const id = element.getAttribute('data-code') || element.getAttribute('data-id');
                        if (!id) return false;
                        const text = element.textContent || '';
                        const label = text.replace(/^@/, '').trim();
                        return {
                            id: id,
                            label: label || id,
                        };
                    },
                },
            ];
        },
    });

    // @ts-ignore
  const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false,
                paragraph: {
                    HTMLAttributes: {
                        class: 'editor-paragraph',
                    },
                },
            }),
            EditorLink,
            Color,
            TextStyle,
            Underline,
            CustomImage.configure({
                inline: true,
                allowBase64: true,
                HTMLAttributes: {
                    class: 'editor-image',
                },
            }),
            CustomMention.configure({
                HTMLAttributes: {
                    class: 'member',
                },
                renderLabel({ node }) {
                    return `@${node.attrs.label || node.attrs.id}`;
                },
            }),
            Placeholder.configure({ placeholder: '添加正文...' }),
        ],
        content: prepareEditorHtmlForHydration(replaceEmojiCodes(content, emojiList)),
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onContentChange?.(html);
        },
        editorProps: {
            attributes: {
                class: 'tiptap-editor-content tiptap-editor-content--pc',
            },
            handlePaste: (view, event) => {
                // 获取粘贴的数据
                const items = event.clipboardData?.items;
                if (!items) return false;

                const pasteCallback = (type, param) => {
                    if (type === 'image') {
                        // 显示上传中状态
                        return setUploadingImages(param);
                    } else if (type === 'link') {
                        return validateLinkWithWhitelist(param, whiteList, whiteHttpList);
                    }
                    return true;
                }
                let type = getContentType(event.clipboardData.types)
                if (type === 'html' || type === 'file') {
                    // 获取图片数据
                    pasteContent(event, editor, content, pasteCallback)
                    return true;
                }
                return false;
            },
        },
    });

    // 监听 content 变化，更新编辑器内容（用于编辑时回显）
    useEffect(() => {
        if (!editor) return;

        const parsedContent = prepareEditorHtmlForHydration(replaceEmojiCodes(content, emojiList));
        const currentHtml = editor.getHTML();
        const sourceChanged = lastHydratedSourceRef.current !== content;
        const editorMatchesLastHydrated = currentHtml === lastHydratedHtmlRef.current;

        if ((sourceChanged || editorMatchesLastHydrated) && currentHtml !== parsedContent) {
            editor.commands.setContent(parsedContent, { parseOptions: { preserveWhitespace: 'full' } });
        }

        lastHydratedSourceRef.current = content;
        lastHydratedHtmlRef.current = parsedContent;
    }, [content, editor, emojiList]);

    // 自定义插入链接弹窗
    const handleLink = (editor: any) => {
        setPendingEditor(editor);
        setLinkUrl('');
        setLinkText(editor.state.selection.empty ? '' : editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to));
        setShowLinkModal(true);
    };

    const handleLinkConfirm = () => {
        if (!linkUrl.trim()) {
            window.alert('请输入链接地址！');
            return;
        }

        // 白名单校验
        if (!validateLinkWithWhitelist(linkUrl.trim(), whiteList, whiteHttpList)) {
            window.alert('该链接不在白名单中，无法添加！');
            return;
        }

        if (/^https?:\/\//.test(linkUrl.trim())) {
            const text = linkText || linkUrl;
            // 如果有选区则直接设置链接，否则插入新文本并设置链接
            if (pendingEditor.state.selection.empty) {
                pendingEditor.chain().focus().insertContent(text).extendMarkRange('link').setLink({ href: linkUrl.trim(), target: '_blank' }).run();
            } else {
                pendingEditor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim(), target: '_blank' }).run();
            }
            setShowLinkModal(false);
        } else {
            window.alert('请输入有效的 http(s) 链接！');
        }
    };

    // 表情选择处理
    const handleEmojiSelect = (emoji: string, emojiCode?: string, meta?: { category?: string }) => {
        if (!editor) return;

        const isDefaultCategory = meta?.category === '默认';
        const defaultEmojiSize = 28;

        if (emoji && emoji.startsWith && emoji.startsWith('http')) {
            const size = isDefaultCategory ? defaultEmojiSize : 59;
            const attrs = isDefaultCategory
                ? ({alt:emojiCode, src: emoji, 'data-emoji': 'default', width: size, height: size } as any)
                : ({alt:emojiCode, src: emoji, 'data-emoji': 'large', width: size, height: size } as any);

            // 优先用 setImage（支持 schema），失败再用 HTML 兜底
            const ok = editor.chain().focus().setImage(attrs).insertContent({ type: 'text', text: ' ' }).run();
            if (!ok) {
                editor.chain().focus()
                    .insertContent(`<img src="${emoji}" alt="emoji" data-emoji="${isDefaultCategory ? 'default' : 'large'}" style="width: ${size}px; height: ${size}px; display: inline-block; vertical-align: middle;" />`)
                    .insertContent({ type: 'text', text: ' ' })
                    .run();
            }
        } else {
            // 默认 emoji 文本或 code
            editor.chain().focus().insertContent(emoji).insertContent({ type: 'text', text: ' ' }).run();
        }

    };

    // @好友选择处理
    const handleMentionSelect = (friends: any[]) => {
        const mergedMentions = mergeMentions(currentMentions, friends || []);
        const mentionLimit = getMentionLimit(currentUserInfo);

        if (Number.isFinite(mentionLimit) && mergedMentions.length > mentionLimit) {
            Toast.error(`为维护友好的交流氛围，单条内容最多支持@${mentionLimit}位好友`);
            return false;
        }

        if (editor && friends.length > 0) {
            friends.forEach((friend, index) => {
                editor
                    .chain()
                    .focus()
                    .insertContent([
                        { type: 'mention', attrs: { id: friend.id, label: friend.name } },
                        { type: 'text', text: index === friends.length - 1 ? ' ' : ' ' },
                    ])
                    .run();
            });
        }
        onContentChange?.(editor?.getHTML?.() || content || '');
        return true;
    };

    // 话题选择处理
    const handleTopicSelect = (topics: any[]) => {
        console.log('[TiptapEditor] handleTopicSelect - selected topics:', topics);
        if (topics && topics.length > 0) {
            // TopicPicker 返回的已是最终选择，直接使用而不是合并
            const newTopics = topics.slice(0, 3);
            // 同时更新本地状态（即时反馈）和父组件（持久化）
            setSelectedTopics(newTopics);
            onTopicSelect?.(newTopics);
        }
        console.log('[TiptapEditor] closing TopicPicker');
        setShowTopicPicker(false);
    };

    // 删除话题
    const handleRemoveTopic = (index: number) => {
        const newTopics = selectedTopics.filter((_, idx) => idx !== index);
        // 同时更新本地状态和父组件
        setSelectedTopics(newTopics);
        onTopicSelect?.(newTopics);
    };

    // 图片上传处理
    const handleImageClick = () => {
        imageInputRef.current?.click();
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const currentImageCount = countNormalImagesFromHtml(editor?.getHTML() || content || '');
        const remainingSlots = MAX_THREAD_IMAGES - currentImageCount;
        if (remainingSlots <= 0) {
            Toast.error(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片`);
            e.target.value = '';
            return;
        }

        const selectedFiles = Array.from(files);
        const filesToUpload = selectedFiles.slice(0, remainingSlots);
        if (filesToUpload.length < selectedFiles.length) {
            Toast.error(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片，超出的图片未添加`);
        }

        setUploadingImages(true);

        for (const file of filesToUpload) {
            try {
                // 验证文件类型
                if (!isSupportedImageFile(file)) {
                    Toast.error('请选择图片文件');
                    continue;
                }

                // 验证文件大小 (限制 50MB)
                if (file.size > 50 * 1024 * 1024) {
                    Toast.error('图片大小不能超过 50MB');
                    continue;
                }

                // 创建 FormData 并上传
                const formData = new FormData();
                formData.append('type', '1'); // 1 = 图片
                formData.append('file', file);

                const closeLoading = Toast.loading({
                    content: '图片上传中，请稍候...',
                    duration: 0,
                });
                const response = await uploadAttachment(formData).finally(closeLoading);
                const imageData = response.data as { url?: string; webpUrl?: string } | undefined;
                const imageUrl = imageData?.webpUrl || imageData?.url;

                if (response.code === 0 && imageUrl) {
                    // 上传成功，插入真实 URL
                    if (editor) {
                        editor.chain().focus().setImage({ src: imageUrl }).run();
                    }
                    const updatedImageCount = countNormalImagesFromHtml(editor?.getHTML() || content || '');
                    if (updatedImageCount >= MAX_THREAD_IMAGES) {
                        Toast.info(`已达到图文图片上限 ${MAX_THREAD_IMAGES} 张`);
                    }
                } else {
                    Toast.error(response.message || '图片上传失败');
                }
            } catch (error: any) {
                console.error('图片上传失败:', error);
                Toast.error(error.message || '图片上传失败，请重试');
            }
        }

        setUploadingImages(false);
        // 清空 input 值，允许重复选择同一文件
        e.target.value = '';
    };

    // 视频上传处理
    const handleVideoClick = () => {
        // 检查是否已有视频
        if (hasVideo) {
            alert('只可添加1个视频');
            return;
        }
        videoInputRef.current?.click();
    };

    const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('video/')) {
            Toast.error('请选择视频文件');
            return;
        }

        // 验证文件大小 (限制 500MB)
        if (file.size > 500 * 1024 * 1024) {
            Toast.error('视频大小不能超过 500MB');
            return;
        }

        // 显示加载中
        const closeLoading = Toast.loading({
            content: '视频上传中，请稍候...',
            duration: 0,
        });

        try {
            // 立即上传视频到 OSS
            const uploadDir = 'public/videos/' + new Date().toISOString().split('T')[0].replace(/-/g, '/');
            const result = await uploadOssFile(file, uploadDir, (percent) => {
            });

            closeLoading?.();

            if (result.code !== 0) {
                Toast.error(result.msg || '视频上传失败');
                return;
            }

            // 上传成功，获取返回的视频信息
            const videoData = result.data;

                const ossVideoUrl = videoData.url || videoData.ossVideoUrl;

                if (ossVideoUrl && onVideoUpload) {
                    // 直接传递 OSS URL和文件对象、完整数据
                    onVideoUpload(ossVideoUrl, file, videoData);
                }

        } catch (error: any) {
            closeLoading?.();
            console.error('[TiptapEditor] 视频上传异常:', error);
            Toast.error(error?.message || '视频上传失败，请重试');
        }

        // 清空 input 值
        e.target.value = '';
    };

    // 投票处理
    const handleVoteConfirm = (voteData: any) => {
        // 关闭弹窗
        if (onVoteModalClose) {
            onVoteModalClose();
        } else {
            setInternalShowVoteModal(false);
        }
        // 调用回调
        if (onVoteCreate) {
            onVoteCreate(voteData);
        }
    };

    const handleVoteModalOpen = () => {
        if (onVoteModalOpen) {
            // 如果有外部控制，调用父组件的打开方法
            onVoteModalOpen();
        } else {
            // 否则使用内部状态
            setInternalShowVoteModal(true);
        }
    };
    // 订阅编辑器选择/事务更新，及时刷新图标状态
    useEffect(() => {
        if (!editor) return;
        const onSelection = () => setRefreshTick(t => t + 1);
        const onUpdate = () => setRefreshTick(t => t + 1);
        editor.on('selectionUpdate', onSelection);
        editor.on('transaction', onUpdate);
        editor.on('update', onUpdate);
        return () => {
            editor.off('selectionUpdate', onSelection);
            editor.off('transaction', onUpdate);
            editor.off('update', onUpdate);
        };
    }, [editor]);

    return (
        <div className={styles.editorOuterWrap}>
            <div className={styles.toolbarWrap} style={{ position: 'relative' }} ref={toolbarWrapRef}>
                <div className={styles.toolbar}>
                    {toolbarActions.map((action, idx) => {
                        // 根据权限过滤按钮
                        if (action.name === 'image' && !otherPermissions?.canInsertThreadImage) {
                            return null;
                        }
                        if (action.name === 'video' && !otherPermissions?.canInsertThreadVideo) {
                            return null;
                        }
                        if (action.name === 'vote' && !otherPermissions?.canInsertThreadVote) {
                            return null;
                        }

                        return (
                            <div
                                className={styles.toolItem}
                                key={action.name}
                                ref={action.name === 'color' ? colorTriggerRef : undefined}
                                onClick={e => {
                                    if (action.name === 'link') {
                                        editor && handleLink(editor);
                                    } else if (action.name === 'color') {
                                        setShowColorModal(prev => !prev);
                                    } else if (action.name === 'emoji') {
                                        updateEmojiPickerPosition(e.currentTarget as HTMLElement);
                                        setShowEmojiPicker(!showEmojiPicker);
                                    } else if (action.name === 'mention') {
                                        setShowMentionPicker(!showMentionPicker);
                                    } else if (action.name === 'topic') {
                                        setShowTopicPicker(!showTopicPicker);
                                    } else if (action.name === 'image') {
                                        handleImageClick();
                                    } else if (action.name === 'video') {
                                        handleVideoClick();
                                    } else if (action.name === 'vote') {
                                        handleVoteModalOpen();
                                    } else {
                                        editor && action.command(editor);
                                    }
                                }}
                            >
                                <div className={styles.toolIcon}>
                                    {(() => {
                                        const activeNameMap: Record<string, string> = {
                                            blodFont: 'blodFontActive',
                                            italic: 'italicActive',
                                            strikethrough: 'strikethroughActive',
                                            colorBar: 'colorBarActive',
                                            links: 'linksActive',
                                            expression: 'expressionActive',
                                            photoBar: 'photoBarActive',
                                            videoBar: 'videoBarActive',
                                            vote: 'voteActive',
                                            topic: 'topicActive',
                                            atfirends: 'atfriendsActive',
                                        };

                                        const isActive = (() => {
                                            if (!editor) return false;
                                            switch (action.name) {
                                                case 'bold':
                                                    return editor.isActive('bold');
                                                case 'italic':
                                                    return editor.isActive('italic');
                                                case 'strike':
                                                    return editor.isActive('strike');
                                                case 'link':
                                                    return editor.isActive('link');
                                                case 'color': {
                                                    const colorAttr = editor.getAttributes('textStyle')?.color;
                                                    return !!colorAttr || showColorModal;
                                                }
                                                case 'emoji':
                                                    return false; // 不需要点亮
                                                case 'mention':
                                                    return false; // 不需要点亮
                                                case 'topic':
                                                    return showTopicPicker || selectedTopics.length > 0;
                                                case 'image':
                                                    return false; // 不需要点亮
                                                case 'video':
                                                    return false; // 不需要点亮
                                                case 'vote':
                                                    return false; // 不需要点亮
                                                default:
                                                    return false;
                                            }
                                        })();

                                        const iconKey = (isActive && activeNameMap[action.icon]) ? activeNameMap[action.icon] : action.icon;
                                        return (
                                            <img
                                                className={styles.toolImg}
                                                src={setPostImage[iconKey as keyof typeof setPostImage]}
                                                alt={action.label}
                                            />
                                        );
                                    })()}
                                </div>
                                <div className={styles.toolLabel}>{action.label}</div>
                            </div>
                        );
                    })}
                </div>
                {showColorModal && (
                    <div className={styles.colorModalWrap} ref={colorModalRef}>
                        <div className={styles.colorModalBox}>
                            {COLOR_LIST.map(color => (
                                <div
                                    key={color}
                                    className={styles.colorBlock}
                                    style={{ background: color }}
                                    onClick={() => {
                                        editor && editor.chain().focus().setColor(color).run();
                                        setShowColorModal(false);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
                {showEmojiPicker && (
                    <EmojiPicker
                        visible={showEmojiPicker}
                        onClose={() => setShowEmojiPicker(false)}
                        onSelect={handleEmojiSelect}
                        showEmojiTab
                        closeOnSelect={false}
                        position={emojiPickerPosition}
                    />
                )}
                {showMentionPicker && (
                    <MentionPicker
                        visible={showMentionPicker}
                        onClose={() => setShowMentionPicker(false)}
                        onSelect={handleMentionSelect}
                        position={{ top: 80, left: 100 }}
                    />
                )}
                {showTopicPicker && (
                    <TopicPicker
                        visible={showTopicPicker}
                        onClose={() => setShowTopicPicker(false)}
                        onSelect={handleTopicSelect}
                        topicList={topicList}
                        categoryList={categoryList}
                        onCategoryChange={onCategoryChange}
                        initialSelected={selectedTopics}
                        position={{ top: 100, left: 200 }}
                        topicStore={topicStore}
                        hasMore={hasMore}
                        isLoadingMore={isLoadingMore}
                    />
                )}
            </div>
            {/* 话题标签显示在toolbar下方 */}
            <div className={styles.topicListWrap}>
                {selectedTopics.map((topic, idx) => (
                    <div className={styles.topicTag} key={topic.id}>
                        <img className={styles.topicIcon} src={setPostImage.topicActive} alt="#" />
                        <span className={styles.topicText}>{topic.name}</span>
                        <span className={styles.topicClose} onClick={() => handleRemoveTopic(idx)}>×</span>
                    </div>
                ))}
            </div>
            {/* 投票弹窗 */}
            <VoteModal
                visible={showVoteModal}
                onClose={onVoteModalClose || (() => setInternalShowVoteModal(false))}
                onConfirm={handleVoteConfirm}
                initialData={externalVoteData}
            />
            <div className={styles.tiptapEditorWrap}>
                <EditorContent editor={editor} />
            </div>
            {/* 隐藏的图片上传input */}
            <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageChange}
            />
            {/* 隐藏的视频上传input */}
            <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleVideoChange}
            />

            {showLinkModal && (
                <div className={styles.linkModalMask}>
                    <div className={styles.linkModalBox}>
                        <div className={styles.linkModalTitle}>插入链接</div>
                        <input
                            className={styles.linkInput}
                            placeholder="请输入链接文本"
                            value={linkText}
                            onChange={e => setLinkText(e.target.value)}
                        />
                        <input
                            className={styles.linkInput}
                            placeholder="请输入 http(s) 链接地址"
                            value={linkUrl}
                            onChange={e => setLinkUrl(e.target.value)}
                        />
                        <div className={styles.linkModalBtns}>
                            <button className={styles.linkBtn} onClick={handleLinkConfirm}>确定</button>
                            <button className={styles.linkBtn} onClick={() => setShowLinkModal(false)}>取消</button>
                        </div>
                    </div>
                </div>
            )}
            {uploadingImages && (
                <div className={styles.uploadingMask}>
                    <div className={styles.uploadingBox}>
                        {/* <p>图片上传中...</p> */}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TiptapEditor;
